import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { alpha, useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import { formatCurrency } from "helpers/utils";
import { formatDate } from "helpers/formatDate";
import { chipColorStatus, labelStatus } from "utils/pedidos";
import { labelTipoEntrega } from "utils/pedidoOperacional";
import type { IPagamentoLoja, StatusPedido } from "interfaces/loja";
import type { TipoEntrega } from "interfaces/loja/pedidoOperacional";
import { calcularResumoValoresPedido } from "utils/pedidoValoresUi";

type PedidoCabecalhoProps = {
  numero?: string | null;
  pedidoId: string;
  status: StatusPedido | string;
  tipoEntrega: TipoEntrega | string;
  total: number | string;
  pagamentos?: IPagamentoLoja[];
  compradorNome?: string | null;
  criadoEm?: string | null;
};

export default function PedidoCabecalho({
  numero,
  pedidoId,
  status,
  tipoEntrega,
  total,
  pagamentos,
  compradorNome,
  criadoEm,
}: PedidoCabecalhoProps) {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const borderSoft =
    mode === "dark" ? alpha("#c8e6c9", 0.45) : alpha("#7cb342", 0.22);

  const st = status as StatusPedido;
  const resumo = calcularResumoValoresPedido(total, pagamentos);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${borderSoft}`,
        boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
      }}
    >
      <MDBox p={2.5}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1}
          >
            <Stack spacing={0.5}>
              <Typography variant="h5" component="h2" fontWeight={700}>
                Pedido {numero ?? pedidoId.slice(0, 8)}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: "monospace" }}
              >
                ID: {pedidoId}
              </Typography>
            </Stack>
            <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
              <Chip
                label={labelStatus(String(status))}
                size="small"
                color={chipColorStatus(st)}
                variant={
                  st === "ENTREGUE" || st === "PAGO" ? "filled" : "outlined"
                }
                sx={{ fontWeight: 600, padding: "16px" }}
              />
              <Chip
                label={labelTipoEntrega(tipoEntrega)}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ fontWeight: 600, padding: "16px" }}
              />
              {resumo.temJuros ? (
                <Chip
                  label={`Cobrado: ${formatCurrency(resumo.valorCobrado)}`}
                  size="small"
                  variant="outlined"
                  color="warning"
                  sx={{ fontWeight: 600, padding: "16px" }}
                />
              ) : null}
            </Stack>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
              >
                Valor do pedido
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {formatCurrency(resumo.valorPedido)}
              </Typography>
              {resumo.temJuros ? (
                <Stack spacing={0.25} mt={0.75}>
                  <Typography variant="caption" color="text.secondary">
                    Cobrado no cartão:{" "}
                    <Typography
                      component="span"
                      variant="caption"
                      fontWeight={700}
                      color="warning.main"
                    >
                      {formatCurrency(resumo.valorCobrado)}
                    </Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Juros: {formatCurrency(resumo.valorJuros)}
                  </Typography>
                </Stack>
              ) : null}
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
              >
                Comprador
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {compradorNome?.trim() || "—"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
              >
                Criado em
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {criadoEm ? formatDate(criadoEm, "dd/MM/yyyy HH:mm:ss") : "—"}
              </Typography>
            </Grid>
          </Grid>
        </Stack>
      </MDBox>
    </Card>
  );
}
